"use client";

import { MANGAL_HOUSE_NUMBERS } from "@ishvaram/domain-data/dosha-phrases";
import { useState } from "react";
import Link from "next/link";

interface DoshaItem {
  id: string;
  nameEn: string;
  nameHi: string;
  severity: "High" | "Moderate" | "Condition-Dependent";
  cancellationRule: string;
  hindiCancellation: string;
  remedies: string[];
  hindiRemedies: string[];
  scripturalCitation: string;
}

const DOSHA_REGISTRY: DoshaItem[] = [
  {
    id: "nadi",
    nameEn: "Nadi Dosha (8 Points)",
    nameHi: "नाड़ी दोष (8 अंक)",
    severity: "High",
    cancellationRule:
      "Cancelled if Moon Rashi is same but Nakshatras differ, or if Nakshatras are same but Pada/Charana differs, or if both Rashi lords are mutual friends.",
    hindiCancellation:
      "यदि वर-कन्या की राशि एक हो पर नक्षत्र भिन्न हों, अथवा नक्षत्र एक हो पर चरण अलग हों, या राशि स्वामी मित्र हों, तो नाड़ी दोष स्वतः समाप्त (परिहार) हो जाता है।",
    remedies: [
      "Recite the Maha Mrityunjaya mantra 108 times daily, together through the engagement period.",
      "Chant Vishnu Sahasranama together before the marriage ceremonies.",
      "Daan of grains or cloth to someone in need, within your means and as the family Jyotishi advises.",
    ],
    hindiRemedies: [
      "सगाई से विवाह तक महामृत्युंजय मंत्र का नित्य 108 बार जप करें।",
      "विवाह पूर्व वर-वधू साथ मिलकर विष्णु सहस्रनाम का पाठ करें।",
      "अपनी सामर्थ्य और कुल-ज्योतिषी की सलाह के अनुसार ज़रूरतमंद को अन्न या वस्त्र का दान दें।",
    ],
    scripturalCitation: "Brihat Parashara Hora Shastra & Muhurta Chintamani",
  },
  {
    id: "bhakoot",
    nameEn: "Bhakoot Dosha (7 Points / 6-8, 9-5, 12-2)",
    nameHi: "भकूट दोष (7 अंक / षडाष्टक, नवपंचम, द्विर्द्वादश)",
    severity: "Moderate",
    cancellationRule:
      "Cancelled if both Rashi lords are identical (e.g. Aries-Scorpio ruled by Mars) or mutual natural friends (Sun-Jupiter, Moon-Mars).",
    hindiCancellation:
      "यदि दोनों राशियों का स्वामी एक ही ग्रह हो (जैसे मेष-वृश्चिक) या राशि स्वामी आपस में परम मित्र हों, तो भकूट दोष पूर्णतः निष्प्रभावी हो जाता है।",
    remedies: [
      "Perform Lord Shiva Rudrabhishek with milk and honey on Mondays.",
      "Donate green pulses (Moong) or yellow cloth on Thursdays.",
      "Optional: traditionally some people also wear Pearl or Yellow Sapphire. This is optional and secondary to the practices above; nothing needs to be bought. Consult an astrologer before wearing any gemstone.",
    ],
    hindiRemedies: [
      "सोमवार को भगवान शिव का दुग्ध व मधु से रुद्राभिषेक करें।",
      "गुरुवार को चने की दाल या पीले वस्त्र का दान करें।",
      "वैकल्पिक: परंपरागत रूप से कुछ लोग मोती अथवा पुखराज भी पहनते हैं। यह वैकल्पिक है और ऊपर दिए अभ्यासों के बाद आता है; कुछ भी खरीदना आवश्यक नहीं। कोई भी रत्न पहनने से पहले ज्योतिषी से सलाह अवश्य लें।",
    ],
    scripturalCitation: "Muhurta Chintamani, Vivaha Prakarana",
  },
  {
    id: "mangal",
    nameEn: `Mangal / Manglik Dosha (${MANGAL_HOUSE_NUMBERS} Houses)`,
    nameHi: `मांगलिक दोष (${MANGAL_HOUSE_NUMBERS} भाव)`,
    severity: "Condition-Dependent",
    cancellationRule:
      "Cancelled if Mars sits in own house (Aries/Scorpio) or exaltation (Capricorn), or if partner also has Manglik placement in matching houses.",
    hindiCancellation:
      "यदि मंगल स्वराशि (मेष/वृश्चिक) या उच्च (मकर) में हो, अथवा जीवनसाथी की कुंडली में भी मंगल सम भावों में हो, तो दोष पूर्णतः कट जाता है।",
    remedies: [
      "Perform Kumbh Vivah or Vishnu Pratima Vivah prior to the actual wedding.",
      "Recite Hanuman Chalisa and Sundarkand on Tuesdays.",
      "Feed jaggery and roasted gram (Chana) to monkeys on Tuesdays.",
    ],
    hindiRemedies: [
      "विवाह से पहले कुंभ विवाह (या विष्णु जी के साथ प्रतीकात्मक विवाह) का अनुष्ठान करें।",
      "मंगलवार को सुंदरकांड का पाठ करें और हनुमान जी को सिंदूर चढ़ाएं।",
      "मंगलवार को बंदरों को गुड़-चना खिलाएं।",
    ],
    scripturalCitation: "Phaladeepika & Brihat Samhita",
  },
  {
    id: "kaal-sarp",
    nameEn: "Kaal Sarp Dosha (All Planets between Rahu-Ketu)",
    nameHi: "कालसर्प दोष (राहु-केतु अक्ष)",
    severity: "Condition-Dependent",
    cancellationRule:
      "Cancelled if Jupiter aspects Rahu or Moon sits with Jupiter creating Gajakesari Yoga, or after age 28-32 when Rahu maturation settles.",
    hindiCancellation:
      "यदि गुरु की दृष्टि राहु पर हो अथवा चंद्रमा के साथ गुरु होकर गजकेसरी योग बनाए, तो कालसर्प का दुष्प्रभाव समाप्त हो जाता है।",
    remedies: [
      "Perform Nagbali or Kaal Sarp Shanti Puja at Trimbakeshwar / Kalahasti.",
      "Chant 'Om Namah Shivaya' 108 times daily; traditionally some people count on a Rudraksha mala, which is optional.",
      "Release a pair of silver snakes (Nag-Nagin) into running river water.",
    ],
    hindiRemedies: [
      "त्र्यंबकेश्वर अथवा श्रीकालहस्ती में कालसर्प शांति पूजा करवाएं।",
      "प्रतिदिन 'ॐ नमः शिवाय' का 108 बार जप करें; परंपरागत रूप से कुछ लोग रुद्राक्ष माला से जप करते हैं, जो वैकल्पिक है।",
      "बहते जल में चांदी के नाग-नागिन के जोड़े का प्रवाह करें।",
    ],
    scripturalCitation: "Mansagari & Jataka Parijata",
  },
];

export default function DoshaMitigationStudio({ isHindi = false }: { isHindi?: boolean }) {
  const [selectedDoshaId, setSelectedDoshaId] = useState("nadi");

  const dosha = DOSHA_REGISTRY.find((d) => d.id === selectedDoshaId) || DOSHA_REGISTRY[0];

  return (
    <div className="my-8 rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-7 shadow-xs">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-border-default">
        <div>
          <span className="text-caption font-bold uppercase tracking-wider text-intent-brand">
            {isHindi ? "शास्त्रीय दोष परिहार व निवारण" : "Shastric Dosha Cancellation & Remedies"}
          </span>
          <h2 className="text-2xl font-bold font-serif text-text-primary mt-1">
            {isHindi
              ? "कुंडली दोष परिहार व वैदिक उपाय स्टूडियो"
              : "Vedic Dosha Mitigation & Remedial Action Studio"}
          </h2>
        </div>

        <Link
          href={isHindi ? "/hi/muft-jyotish-chat/" : "/ai-astrologer/"}
          className="inline-flex items-center gap-1.5 rounded-lg bg-surface-brand-strong px-4 py-2 text-caption font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shrink-0"
        >
          <span>✦</span>
          <span>{isHindi ? "दोष निवारण परामर्श लें" : "Consult AI Astrologer"}</span>
        </Link>
      </div>

      {/* Selector Tabs */}
      <div className="my-5 flex flex-wrap gap-2">
        {DOSHA_REGISTRY.map((item) => {
          const isSelected = item.id === selectedDoshaId;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setSelectedDoshaId(item.id)}
              className={`px-3.5 py-2 rounded-xl border text-caption font-bold transition-all ${
                isSelected
                  ? "border-intent-brand bg-surface-brand-subtle text-intent-brand shadow-xs ring-1 ring-intent-brand/30"
                  : "border-border-default bg-surface-raised text-text-secondary hover:bg-surface-warm"
              }`}
            >
              {isHindi ? item.nameHi : item.nameEn}
            </button>
          );
        })}
      </div>

      {/* Detail Card */}
      <div className="rounded-xl border border-border-default bg-surface-raised p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-border-default">
          <h3 className="text-xl font-bold font-serif text-text-primary">
            {isHindi ? dosha.nameHi : dosha.nameEn}
          </h3>
          <span className="text-caption text-text-muted">
            {isHindi ? "शास्त्र प्रमाण: " : "Citation: "}
            <span className="font-semibold text-text-primary">{dosha.scripturalCitation}</span>
          </span>
        </div>

        {/* Cancellation Rule */}
        <div className="my-4 p-4 rounded-xl bg-surface-brand-subtle border border-intent-brand/20">
          <span className="text-caption font-bold uppercase tracking-wider text-intent-brand block mb-1">
            {isHindi
              ? "✓ स्वतः परिहार नियम (Cancellation Conditions):"
              : "✓ Scriptural Cancellation Conditions (Parihar):"}
          </span>
          <p className="text-body-small text-text-primary leading-relaxed">
            {isHindi ? dosha.hindiCancellation : dosha.cancellationRule}
          </p>
        </div>

        {/* Remedies List */}
        <div className="space-y-2.5">
          <span className="text-caption font-bold uppercase tracking-wider text-text-muted block">
            {isHindi
              ? "प्रमाणित वैदिक उपाय (Prescribed Upay):"
              : "Authentic Prescribed Upay & Remedies:"}
          </span>
          <ul className="space-y-2">
            {(isHindi ? dosha.hindiRemedies : dosha.remedies).map((rem, idx) => (
              <li key={idx} className="flex items-start gap-2 text-caption text-text-primary">
                <span className="text-intent-brand font-bold">📿</span>
                <span>{rem}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
